import React, { createContext, useContext, useState } from 'react';

// ── Context ───────────────────────────────────────────────────────────────

interface TabContextValue {
  activeTab: string;
  setActiveTab: (id: string) => void;
}

const TabContext = createContext<TabContextValue>({ activeTab: '', setActiveTab: () => {} });

// ── TabPanelContainer ─────────────────────────────────────────────────────

export interface TabPanelContainerProps {
  /** Default active tab id. Falls back to the first child's id. */
  defaultTab?: string;
  /** Controlled active tab id. */
  activeTab?: string;
  /** Called when the user clicks a tab. */
  onTabChange?: (id: string) => void;
  children: React.ReactNode;
  /** Accent color for the active tab indicator (defaults to #6366f1). */
  accentColor?: string;
  /** Extra style applied to the outer wrapper. */
  style?: React.CSSProperties;
}

export function TabPanelContainer({
  defaultTab,
  activeTab: controlledTab,
  onTabChange,
  children,
  accentColor = '#6366f1',
  style,
}: TabPanelContainerProps) {
  // Collect tab descriptors from children
  const tabs: { id: string; label: React.ReactNode; selectable: boolean }[] = [];
  React.Children.forEach(children, child => {
    if (React.isValidElement(child) && (child.type as any).__isTabPanel) {
      const p = child.props as TabPanelProps;
      tabs.push({ id: p.id, label: p.label, selectable: p.selectable !== false });
    }
  });

  const fallbackDefault = tabs[0]?.id ?? '';
  const [internalTab, setInternalTab] = useState(defaultTab ?? fallbackDefault);

  const isControlled = controlledTab !== undefined;
  const activeTab    = isControlled ? controlledTab! : internalTab;

  const handleSelect = (id: string) => {
    if (!isControlled) setInternalTab(id);
    onTabChange?.(id);
  };

  return (
    <TabContext.Provider value={{ activeTab, setActiveTab: handleSelect }}>
      <div style={{ display: 'flex', flexDirection: 'column', ...style }}>

        {/* Tab bar */}
        <div style={s.tabBar}>
          {tabs.map(tab => {
            const isActive   = tab.id === activeTab;
            const isDisabled = !tab.selectable && !isActive;
            return (
              <button
                key={tab.id}
                disabled={isDisabled}
                onClick={() => !isDisabled && handleSelect(tab.id)}
                style={{
                  ...s.tabBtn,
                  ...(isActive   ? { ...s.tabBtnActive, background: accentColor } : {}),
                  ...(isDisabled ? s.tabBtnDisabled : {}),
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Panels */}
        <div style={s.panelArea}>
          {children}
        </div>

      </div>
    </TabContext.Provider>
  );
}

// ── TabPanel ──────────────────────────────────────────────────────────────

export interface TabPanelProps {
  /** Must be unique within its container. */
  id: string;
  /** Text or node shown in the tab bar button. */
  label: React.ReactNode;
  children?: React.ReactNode;
  /** Extra style applied to the panel wrapper when visible. */
  style?: React.CSSProperties;
  /**
   * Whether the user can click the tab button to select it.
   * Defaults to true. When false, the tab can only be activated
   * programmatically (e.g. via useTabPanel / TabAutoSwitch).
   */
  selectable?: boolean;
}

export function TabPanel({ id, children, style }: TabPanelProps) {
  const { activeTab } = useContext(TabContext);
  if (activeTab !== id) return null;
  return <div style={{ ...s.panel, ...style }}>{children}</div>;
}

// Mark so TabPanelContainer can identify TabPanel children
(TabPanel as any).__isTabPanel = true;

// ── Hook ──────────────────────────────────────────────────────────────────

/** Programmatically switch tabs from inside a TabPanelContainer. */
export function useTabPanel() {
  return useContext(TabContext);
}

// ── Styles ────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  tabBar: {
    display: 'flex', flexWrap: 'wrap', gap: 4,
    marginBottom: 8,
  },
  tabBtn: {
    padding: '6px 14px', fontSize: 12, fontWeight: 600,
    border: 'none', cursor: 'pointer',
    color: '#94a3b8', borderRadius: 6,
    background: '#f1f5f9',
    marginBottom: 2,
    transition: 'color 0.15s, background 0.15s',
    whiteSpace: 'nowrap' as const,
  },
  tabBtnActive: {
    background: '#3b82f6',
    color: '#fff',
  },
  tabBtnDisabled: {
    background: '#f8fafc',
    color: '#cbd5e1',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  panelArea: {
    flex: 1,
  },
  panel: {
    padding: '12px 0',
  },
};
